import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CategoryService, Category } from "@/lib/category-service";
import { RFQService, RFQRequest, RequestType } from "@/lib/rfq-service";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/polymet/hooks/use-analytics";
import { PlusCircleIcon, MinusCircleIcon, InfoIcon } from "lucide-react";

// Custom DatePicker component until ui/date-picker is implemented
const DatePicker: React.FC<{ value: string; onChange: (date: string) => void }> = ({ 
  value, 
  onChange 
}) => {
  return (
    <Input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

// Custom FileUploader component until proper file uploader is implemented
const FileUploader: React.FC<{
  onUploadComplete: (urls: string[]) => void;
  acceptedFileTypes?: string[];
  maxFileSizeMB?: number;
  maxFiles?: number;
}> = ({
  onUploadComplete,
  acceptedFileTypes = ['image/jpeg', 'image/png', 'application/pdf'],
  maxFileSizeMB = 10,
  maxFiles = 5
}) => {
  // Simulate file upload with a button
  const handleClick = () => {
    // Generate a mock file URL based on accepted file types
    const fileExtension = acceptedFileTypes.includes('application/pdf') ? 'pdf' : 
                          acceptedFileTypes.includes('image/jpeg') ? 'jpg' : 
                          acceptedFileTypes.includes('image/png') ? 'png' : 'doc';
                          
    onUploadComplete([`https://example.com/document-${Date.now()}.${fileExtension}`]);
  };

  // Format accepted file types for display
  const formatAcceptedTypes = () => {
    const typeMap: Record<string, string> = {
      'image/jpeg': 'JPG',
      'image/png': 'PNG', 
      'application/pdf': 'PDF',
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX'
    };
    
    return acceptedFileTypes.map(type => typeMap[type] || type.split('/')[1].toUpperCase()).join(', ');
  };

  return (
    <div className="border-2 border-dashed rounded-md p-6 text-center">
      <p className="mb-2">Upload files ({formatAcceptedTypes()})</p>
      <Button type="button" onClick={handleClick}>Select Files</Button>
      <p className="text-xs mt-2 text-muted-foreground">
        Max {maxFileSizeMB}MB per file. Up to {maxFiles} files.
      </p>
    </div>
  );
};

// Form validation interface
interface FormErrors {
  title?: string;
  description?: string;
  request_type?: string;
  category_id?: string;
  subcategory_id?: string;
  product_category_id?: string;
  quantity?: string;
  target_price?: string;
  delivery_date?: string;
  [key: string]: string | undefined;
}

interface RFQFormProps {
  isEditing?: boolean;
  rfqId?: string;
  initialRFQ?: Partial<RFQRequest>;
  onSuccess?: (rfq: RFQRequest) => void;
}

export function RFQForm({ isEditing = false, rfqId, initialRFQ, onSuccess }: RFQFormProps) {
  // Category related state
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [productCategories, setProductCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // Form state
  const [values, setValues] = useState<Partial<RFQRequest>>({
    title: '',
    description: '',
    request_type: 'quote_request',
    status: 'draft',
    category_id: '',
    subcategory_id: '',
    product_category_id: '',
    quantity: undefined,
    target_price: '',
    delivery_date: '',
    ...initialRFQ,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  
  // Additional form state
  const [specifications, setSpecifications] = useState<Array<{key: string; value: string}>>(
    initialRFQ?.specifications 
      ? Object.entries(initialRFQ.specifications).map(([key, value]) => ({ key, value: String(value) }))
      : [{ key: '', value: '' }]
  );
  const [attachments, setAttachments] = useState<string[]>(initialRFQ?.attachments || []);
  const [submitting, setSubmitting] = useState(false);
  
  // Hooks
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { trackProductVisit } = useAnalytics();
  const rfqService = new RFQService();
  const categoryService = new CategoryService();
  
  // Extract prefilled data from URL parameters
  const [prefilledProduct, setPrefilledProduct] = useState<any>(null);
  
  // Handle prefilled data from URL parameters
  useEffect(() => {
    const productId = searchParams.get('productId');
    const productName = searchParams.get('productName');
    const supplierId = searchParams.get('supplierId');
    const supplierName = searchParams.get('supplierName');
    const categoryId = searchParams.get('categoryId');
    const subcategoryId = searchParams.get('subcategoryId');
    const productCategoryId = searchParams.get('productCategoryId');
    const manufacturer = searchParams.get('manufacturer');
    const model = searchParams.get('model');
    const year = searchParams.get('year');
    const condition = searchParams.get('condition');
    const price = searchParams.get('price');
    const currency = searchParams.get('currency');

    if (productId && productName) {
      // Set prefilled product data
      const productData = {
        productId,
        productName,
        supplierId,
        supplierName,
        categoryId,
        subcategoryId,
        productCategoryId,
        manufacturer,
        model,
        year,
        condition,
        price,
        currency
      };
      
      setPrefilledProduct(productData);
      
      // Prefill form values
      setValues(prev => ({
        ...prev,
        title: `Quote Request for ${productName}`,
        description: `I am interested in getting a quote for the ${manufacturer} ${model} ${productName}${year ? ` (${year})` : ''} in ${condition} condition. Please provide your best price and terms.`,
        category_id: categoryId || '',
        subcategory_id: subcategoryId || '',
        product_category_id: productCategoryId || '',
        quantity: 1,
        target_price: price || ''
      }));
      
      // Add product specifications
      const productSpecs = [];
      if (manufacturer) productSpecs.push({ key: 'Manufacturer', value: manufacturer });
      if (model) productSpecs.push({ key: 'Model', value: model });
      if (year) productSpecs.push({ key: 'Year', value: year });
      if (condition) productSpecs.push({ key: 'Condition', value: condition });
      if (price) productSpecs.push({ key: 'Listed Price', value: `${price} ${currency || 'USD'}` });
      
      if (productSpecs.length > 0) {
        setSpecifications([...productSpecs, { key: '', value: '' }]);
      }
    }
  }, [searchParams]);
  
  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const categoriesData = await categoryService.getCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error loading categories:", error);
        toast({
          title: "Error",
          description: "Failed to load categories. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoadingCategories(false);
      }
    };
    
    loadCategories();
  }, []);
  
  // Load subcategories when category changes
  useEffect(() => {
    const loadSubcategories = async () => {
      if (!values.category_id) {
        setSubcategories([]);
        setValues(prev => ({ ...prev, subcategory_id: '', product_category_id: '' }));
        return;
      }
      
      try {
        const subcategoriesData = await categoryService.getSubcategories(values.category_id);
        setSubcategories(subcategoriesData);
        
        // If the current subcategory is not in the new list, clear it
        if (
          values.subcategory_id && 
          !subcategoriesData.some(subcat => subcat.id === values.subcategory_id)
        ) {
          setValues(prev => ({ 
            ...prev, 
            subcategory_id: '',
            product_category_id: '' 
          }));
        }
      } catch (error) {
        console.error("Error loading subcategories:", error);
        toast({
          title: "Error",
          description: "Failed to load subcategories. Please try again.",
          variant: "destructive",
        });
      }
    };
    
    loadSubcategories();
  }, [values.category_id]);
  
  // Load product categories when subcategory changes
  useEffect(() => {
    const loadProductCategories = async () => {
      if (!values.subcategory_id) {
        setProductCategories([]);
        setValues(prev => ({ ...prev, product_category_id: '' }));
        return;
      }
      
      try {
        const productCategoriesData = await categoryService.getSubcategories(values.subcategory_id);
        setProductCategories(productCategoriesData);
        
        // If the current product category is not in the new list, clear it
        if (
          values.product_category_id && 
          !productCategoriesData.some(product => product.id === values.product_category_id)
        ) {
          setValues(prev => ({ ...prev, product_category_id: '' }));
        }
      } catch (error) {
        console.error("Error loading product categories:", error);
        toast({
          title: "Error",
          description: "Failed to load product categories. Please try again.",
          variant: "destructive",
        });
      }
    };
    
    loadProductCategories();
  }, [values.subcategory_id]);
  
  // Form state validation
  const validate = (formValues: Partial<RFQRequest>): FormErrors => {
    const errors: FormErrors = {};
    
    if (!formValues.title?.trim()) {
      errors.title = "Title is required";
    }
    
    if (!formValues.description?.trim()) {
      errors.description = "Description is required";
    }
    
    if (!formValues.request_type) {
      errors.request_type = "Request type is required";
    }
    
    if (!formValues.category_id || formValues.category_id.trim() === '') {
      errors.category_id = "Category is required";
    }
    
    if (!formValues.subcategory_id || formValues.subcategory_id.trim() === '') {
      errors.subcategory_id = "Subcategory is required";
    }
    
    if (!formValues.product_category_id || formValues.product_category_id.trim() === '') {
      errors.product_category_id = "Product category is required";
    }
    
    if (formValues.quantity !== undefined && formValues.quantity <= 0) {
      errors.quantity = "Quantity must be greater than 0";
    }
    
    // Validate target price if provided
    if (formValues.target_price && isNaN(parseFloat(formValues.target_price))) {
      errors.target_price = "Target price must be a valid number";
    }
    
    // Validate delivery date if provided
    if (formValues.delivery_date) {
      const deliveryDate = new Date(formValues.delivery_date);
      const today = new Date();
      
      if (isNaN(deliveryDate.getTime())) {
        errors.delivery_date = "Please enter a valid date";
      } else if (deliveryDate < today) {
        errors.delivery_date = "Delivery date cannot be in the past";
      }
    }
    
    return errors;
  };
  
  // Handle field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };
  
  // Handle select changes
  const handleSelectChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };
  
  // Handle specification changes
  const handleSpecificationChange = (index: number, field: 'key' | 'value', value: string) => {
    const newSpecifications = [...specifications];
    newSpecifications[index][field] = value;
    setSpecifications(newSpecifications);
  };
  
  // Add a new specification
  const addSpecification = () => {
    setSpecifications(prev => [...prev, { key: '', value: '' }]);
  };
  
  // Remove a specification
  const removeSpecification = (index: number) => {
    if (specifications.length <= 1) {
      return;
    }
    
    const newSpecifications = [...specifications];
    newSpecifications.splice(index, 1);
    setSpecifications(newSpecifications);
  };
  
  // Handle file uploads
  const handleFileUpload = (uploadedUrls: string[]) => {
    setAttachments(prev => [...prev, ...uploadedUrls]);
  };
  
  // Remove an attachment
  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form values
    const formErrors = validate(values);
    setErrors(formErrors);
    
    // Mark all fields as touched
    const allTouched = Object.keys(values).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setTouched(allTouched);
    
    // Exit early if there are errors
    if (Object.keys(formErrors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting.",
        variant: "destructive",
      });
      return;
    }
    
    // Start submitting
    setSubmitting(true);
    
    try {
      // Convert specifications to object
      const specsObject = specifications.reduce((acc, spec) => {
        if (spec.key.trim() && spec.value.trim()) {
          acc[spec.key.trim()] = spec.value.trim();
        }
        return acc;
      }, {} as Record<string, string>);
      
      // Create RFQ data with required fields
      const rfqData: RFQRequest = {
        user_id: user?.id || "",
        title: values.title || "",
        description: values.description || "",
        request_type: values.request_type as RequestType || "quote_request",
        status: values.status || "draft",
        category_id: values.category_id && values.category_id.trim() !== '' ? values.category_id : undefined,
        subcategory_id: values.subcategory_id && values.subcategory_id.trim() !== '' ? values.subcategory_id : undefined,
        product_category_id: values.product_category_id && values.product_category_id.trim() !== '' ? values.product_category_id : undefined,
        quantity: values.quantity,
        target_price: values.target_price && values.target_price.trim() !== '' ? values.target_price : undefined,
        delivery_date: values.delivery_date && values.delivery_date.trim() !== '' ? values.delivery_date : undefined,
        specifications: specsObject,
        attachments: attachments,
        // Include supplier_id if this is a targeted product quote
        supplier_id: prefilledProduct?.supplierId || undefined,
      };
      
      // Create or update RFQ
      let result;
      if (isEditing && rfqId) {
        result = await rfqService.updateRFQ(rfqId, rfqData);
        toast({
          title: "Success",
          description: values.request_type === "general_inquiry" 
            ? "Inquiry updated successfully" 
            : "RFQ updated successfully",
        });
      } else {
        result = await rfqService.createRFQ(rfqData);
        const isProductQuote = prefilledProduct?.productName;
        
        // Track product interaction if this is a product quote
        if (isProductQuote && prefilledProduct.productId) {
          try {
            await trackProductVisit({
              productId: prefilledProduct.productId,
              supplierId: prefilledProduct.supplierId,
              categoryId: prefilledProduct.categoryId,
              subcategoryId: prefilledProduct.subcategoryId,
              productCategoryId: prefilledProduct.productCategoryId,
              source: 'direct',
              interactionType: 'rfq',
              timeSpent: 120 // Estimate 2 minutes for form completion
            });
          } catch (trackingError) {
            console.error('Error tracking product RFQ interaction:', trackingError);
          }
        }
        
        toast({
          title: "Success",
          description: isProductQuote
            ? `Quote request sent to ${prefilledProduct.supplierName || 'supplier'} for ${prefilledProduct.productName}`
            : values.request_type === "general_inquiry" 
              ? "Inquiry submitted successfully" 
              : "RFQ created successfully",
        });
      }
      
      // Call onSuccess callback if provided
      if (onSuccess && result) {
        onSuccess(result);
      } else {
        // Navigate back
        navigate(-1);
      }
    } catch (error) {
      console.error("Error submitting RFQ:", error);
      toast({
        title: "Error",
        description: "Failed to submit the form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Product Information Card - Show when RFQ is for a specific product */}
      {prefilledProduct && (
        <Alert className="bg-blue-50 border-blue-200">
          <InfoIcon className="h-4 w-4 text-blue-600" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">
                  Requesting Quote for: {prefilledProduct.productName}
                </h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p><strong>Manufacturer:</strong> {prefilledProduct.manufacturer}</p>
                  <p><strong>Model:</strong> {prefilledProduct.model}</p>
                  {prefilledProduct.year && <p><strong>Year:</strong> {prefilledProduct.year}</p>}
                  <p><strong>Condition:</strong> {prefilledProduct.condition}</p>
                  {prefilledProduct.supplierName && (
                    <p><strong>Supplier:</strong> {prefilledProduct.supplierName}</p>
                  )}
                  {prefilledProduct.price && (
                    <p><strong>Listed Price:</strong> {prefilledProduct.price} {prefilledProduct.currency || 'USD'}</p>
                  )}
                </div>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              name="title"
              value={values.title || ""}
              onChange={handleChange}
              placeholder="Enter title"
              className={errors.title && touched.title ? "border-red-500" : ""}
            />
            {errors.title && touched.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={values.description || ""}
              onChange={handleChange}
              placeholder="Enter description"
              className={`min-h-[120px] ${errors.description && touched.description ? "border-red-500" : ""}`}
            />
            {errors.description && touched.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="request_type">Request Type</Label>
            <Select
              value={values.request_type || "quote_request"}
              onValueChange={(value) => handleSelectChange("request_type", value)}
            >
              <SelectTrigger 
                className={errors.request_type && touched.request_type ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select request type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="quote_request">Quote Request</SelectItem>
                <SelectItem value="rfp">Request for Proposal (RFP)</SelectItem>
                <SelectItem value="rfi">Request for Information (RFI)</SelectItem>
                <SelectItem value="general_inquiry">General Inquiry</SelectItem>
              </SelectContent>
            </Select>
            {errors.request_type && touched.request_type && (
              <p className="text-red-500 text-sm mt-1">{errors.request_type}</p>
            )}
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="category_id">Category</Label>
            <Select
              value={values.category_id || ""}
              onValueChange={(value) => handleSelectChange("category_id", value)}
              disabled={loadingCategories}
            >
              <SelectTrigger 
                className={errors.category_id && touched.category_id ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category_id && touched.category_id && (
              <p className="text-red-500 text-sm mt-1">{errors.category_id}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="subcategory_id">Subcategory</Label>
            <Select
              value={values.subcategory_id || ""}
              onValueChange={(value) => handleSelectChange("subcategory_id", value)}
              disabled={!values.category_id || subcategories.length === 0}
            >
              <SelectTrigger 
                className={errors.subcategory_id && touched.subcategory_id ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select subcategory" />
              </SelectTrigger>
              <SelectContent>
                {subcategories.map((subcategory) => (
                  <SelectItem key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.subcategory_id && touched.subcategory_id && (
              <p className="text-red-500 text-sm mt-1">{errors.subcategory_id}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="product_category_id">Product Category</Label>
            <Select
              value={values.product_category_id || ""}
              onValueChange={(value) => handleSelectChange("product_category_id", value)}
              disabled={!values.subcategory_id || productCategories.length === 0}
            >
              <SelectTrigger 
                className={errors.product_category_id && touched.product_category_id ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select product category" />
              </SelectTrigger>
              <SelectContent>
                {productCategories.map((productCategory) => (
                  <SelectItem key={productCategory.id} value={productCategory.id}>
                    {productCategory.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.product_category_id && touched.product_category_id && (
              <p className="text-red-500 text-sm mt-1">{errors.product_category_id}</p>
            )}
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="quantity">Quantity</Label>
            <Input
              id="quantity"
              name="quantity"
              type="number"
              value={values.quantity || ""}
              onChange={handleChange}
              placeholder="Enter quantity"
              min="1"
              className={errors.quantity && touched.quantity ? "border-red-500" : ""}
            />
            {errors.quantity && touched.quantity && (
              <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="target_price">Target Price (optional)</Label>
            <Input
              id="target_price"
              name="target_price"
              value={values.target_price || ""}
              onChange={handleChange}
              placeholder="Enter target price"
            />
          </div>
          
          <div>
            <Label htmlFor="delivery_date">Delivery Date (optional)</Label>
            <DatePicker
              value={values.delivery_date || ""}
              onChange={(date) => handleSelectChange("delivery_date", date)}
            />
          </div>
        </div>
        
        <div>
          <Card>
            <CardContent className="pt-6">
              <Label className="mb-2 block">Specifications (optional)</Label>
              {specifications.map((spec, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <div className="grid grid-cols-2 gap-2 flex-1">
                    <Input
                      placeholder="Key"
                      value={spec.key}
                      onChange={(e) => handleSpecificationChange(index, 'key', e.target.value)}
                    />
                    <Input
                      placeholder="Value"
                      value={spec.value}
                      onChange={(e) => handleSpecificationChange(index, 'value', e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeSpecification(index)}
                    disabled={specifications.length === 1}
                  >
                    <MinusCircleIcon className="h-5 w-5" />
                  </Button>
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSpecification}
                className="mt-2"
              >
                <PlusCircleIcon className="h-4 w-4 mr-2" />
                Add Specification
              </Button>
            </CardContent>
          </Card>
        </div>
        
        <div>
          <Label className="mb-2 block">Attachments (optional)</Label>
          <FileUploader 
            onUploadComplete={handleFileUpload}
            acceptedFileTypes={[
              'image/jpeg', 
              'image/png', 
              'application/pdf', 
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ]}
            maxFileSizeMB={10}
            maxFiles={5}
          />
          
          {attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              <Label>Uploaded Files</Label>
              <div className="grid grid-cols-1 gap-2">
                {attachments.map((url, index) => (
                  <div key={index} className="flex items-center justify-between border rounded-md p-2">
                    <div className="truncate max-w-[80%]">
                      {url.split('/').pop()}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="md:col-span-2">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <Checkbox
                id="publish"
                checked={values.status === 'published'}
                onCheckedChange={(checked) => {
                  handleSelectChange("status", checked ? "published" : "draft");
                }}
              />
              <Label htmlFor="publish" className="text-sm font-normal">
                {values.request_type === "general_inquiry" 
                  ? "Send this inquiry immediately (otherwise save as draft)"
                  : "Publish this RFQ immediately (otherwise save as draft)"}
              </Label>
            </div>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting 
                  ? "Submitting..." 
                  : isEditing 
                    ? values.request_type === "general_inquiry" ? "Update Inquiry" : "Update RFQ"
                    : values.request_type === "general_inquiry" ? "Submit Inquiry" : "Create RFQ"
                }
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
